import React, { useMemo, useState } from "react";
import { BreadCrumb } from "primereact/breadcrumb";
import { Button } from "primereact/button";
import { TabView, TabPanel } from "primereact/tabview";
import { useHistory } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { handlePostRequest } from "../../services/PostTemplate";
import { toast } from "react-toastify";
import { Dialog } from "primereact/dialog";
import AddStaffDialog from "./AddStaffDialog";
import SofStop from "../../modals/SofStop";
import { Tooltip } from "react-tippy";
import ClipLoader from "react-spinners/ClipLoader";
import { GATED_SHIFT_ROLES } from "../../services/shiftService";
import styles from "./staff.module.scss";

const STAFF_ROLES = GATED_SHIFT_ROLES;
const ROLE_LABELS = { clerk: "Clerk", handler: "Handler", airportStaff: "Airport Staff", driver: "Driver", admin: "Admin" };

function StaffTable({ roles, addLabel, dialogTitle, breadLabel, users, loading, onChanged }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [selectedRow, setSelectedRow] = useState([]);
    const [showDialog, setShowDialog] = useState(false);
    const [showSoftStop, setShowSoftStop] = useState(false);
    const emptyFilters = { search: "", role: "" };
    const [filters, setFilters] = useState(emptyFilters);

    const filteredUsers = useMemo(() => {
        const search = filters.search.trim().toLowerCase();
        return (users || []).filter((user) => {
            const matchesSearch = !search || [user.firstName, user.lastName, user.email, user.phone].some((field) => field?.toLowerCase().includes(search));
            const matchesRole = !filters.role || user.role === filters.role;
            return matchesSearch && matchesRole;
        });
    }, [users, filters]);

    const handleFilterChange = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }));
    const clearFilters = () => setFilters(emptyFilters);

    const actionBodyTemplate = (rowData) => (
        <div>
            <Button icon="pi pi-ellipsis-v" className="p-button-rounded mr-2 Elipse_Icon" onClick={(e) => handleActionButton(e, rowData)} aria-controls="popup_menu" aria-haspopup />
        </div>
    );
    const handleActionButton = (e, rowdata) => {
        e.preventDefault();
        history.push(`/staff/${rowdata?._id}`);
    };

    const roleBodyTemplate = (rowData) => ROLE_LABELS[rowData.role] || rowData.role;

    const onsuccess = () => {
        setShowDialog(false);
        toast.success(`${dialogTitle} added`);
        onChanged();
    };

    const onSuccessSoftStop = async () => {
        setShowSoftStop(false);
        if (selectedRow?.length > 0) {
            const selectedIds = selectedRow.map((val) => val?._id);
            const res = await dispatch(handlePostRequest({ id: selectedIds }, "/deleteUser", true, true));
            if (res?.success) {
                toast.success(`${dialogTitle} deleted`);
                setSelectedRow([]);
                onChanged();
            }
        }
    };

    return (
        <div>
            <Dialog visible={showDialog} header={dialogTitle} style={{ width: "650px" }} onHide={() => setShowDialog(false)}>
                <AddStaffDialog roles={roles} onsuccess={onsuccess} />
            </Dialog>
            <Dialog visible={showSoftStop} style={{ width: "650px" }} onHide={() => setShowSoftStop(false)}>
                <SofStop onHideSoftStop={() => setShowSoftStop(false)} onSuccessSoftStop={onSuccessSoftStop} type="user"></SofStop>
            </Dialog>

            <div className={styles.tabActions}>
                <Tooltip title={addLabel} position="bottom" trigger="mouseenter" arrow={true}>
                    <Button icon="pi pi-plus" label={addLabel} iconPos="right" onClick={() => setShowDialog(true)} className={styles.addButton} />
                </Tooltip>
                <Tooltip title="Delete selected" position="bottom" trigger="mouseenter" arrow={true}>
                    <Button icon="pi pi-trash" label="Delete" iconPos="right" onClick={() => setShowSoftStop(true)} className={styles.deleteButton} disabled={!selectedRow.length} />
                </Tooltip>
            </div>

            <div className={styles.filtersSection}>
                <div className={styles.filterGroup}>
                    <label>Search</label>
                    <input className={styles.searchInput} placeholder="Search by name, email or phone..." value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)} />
                </div>
                {roles.length > 1 && (
                    <div className={styles.filterGroup}>
                        <label>Role</label>
                        <select className={styles.dropdown} value={filters.role} onChange={(e) => handleFilterChange("role", e.target.value)}>
                            <option value="">All Roles</option>
                            {roles.map((r) => (
                                <option key={r} value={r}>
                                    {ROLE_LABELS[r] || r}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <Button label="Clear" icon="pi pi-times" className={styles.clearButton} onClick={clearFilters} />
            </div>

            <div className={styles.tableContainer}>
                <DataTable
                    paginator
                    rows={100}
                    rowsPerPageOptions={[100, 500, 1000]}
                    className="datatable-responsive custom-datatable"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Records"
                    emptyMessage={`No ${breadLabel.toLowerCase()} found.`}
                    value={filteredUsers}
                    selection={selectedRow}
                    onSelectionChange={(e) => setSelectedRow(e.value)}
                    sortMode="multiple"
                    loadingIcon="pi pi-spinner"
                    loading={loading}
                    loadingTemplate={<ClipLoader color={"#2C4870"} loading={loading} size={30} />}
                >
                    <Column selectionMode="multiple" style={{ width: "3em" }} />
                    <Column header="Action" body={actionBodyTemplate} />
                    <Column field="firstName" header="First Name" sortable />
                    <Column field="lastName" header="Last Name" sortable />
                    <Column field="email" header="Email" sortable />
                    <Column header="Role" body={roleBodyTemplate} sortable field="role" />
                    <Column field="phone" header="Phone Number" sortable />
                    <Column header="Created On" field="date" />
                </DataTable>
            </div>
        </div>
    );
}

function Staff() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    const breadItems = [{ label: "Home", url: "/" }, { label: "Staff" }];
    const home = { icon: "pi pi-home", url: "/" };

    const getUsers = async () => {
        setLoading(true);
        const res = await handleGetRequest("/allUsers");
        setUsers(res?.data || []);
        setLoading(false);
    };

    useMemo(() => {
        getUsers();
    }, []);

    const staffUsers = useMemo(() => (users || []).filter((u) => STAFF_ROLES.includes(u.role)), [users]);
    const adminUsers = useMemo(() => (users || []).filter((u) => u.role === "admin"), [users]);

    return (
        <div className={styles.staffPage}>
            <div className={styles.pageHeader}>
                <div>
                    <h2>Staff</h2>
                    <BreadCrumb model={breadItems} home={home} />
                </div>
            </div>

            <div className={styles.statsRow}>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{staffUsers.length}</div>
                    <div className={styles.statLabel}>Staff</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statValue}>{adminUsers.length}</div>
                    <div className={styles.statLabel}>Admins</div>
                </div>
            </div>

            <TabView>
                <TabPanel header={`Staff Accounts (${staffUsers.length})`} leftIcon="pi pi-users">
                    <StaffTable roles={STAFF_ROLES} addLabel="Add Staff" dialogTitle="Staff Member" breadLabel="Staff" users={staffUsers} loading={loading} onChanged={getUsers} />
                </TabPanel>
                <TabPanel header={`Admin Accounts (${adminUsers.length})`} leftIcon="pi pi-shield">
                    <StaffTable roles={["admin"]} addLabel="Add Admin" dialogTitle="Admin Account" breadLabel="Admins" users={adminUsers} loading={loading} onChanged={getUsers} />
                </TabPanel>
            </TabView>
        </div>
    );
}

export default Staff;
